// Per-tool SEO content for TinyPDFs.
//
// Each entry drives:
//   - document.title           (TOOL.title | brand suffix)
//   - meta description          (TOOL.description, ~150-160 chars)
//   - JSON-LD SoftwareApplication (keywords, description)
//   - JSON-LD FAQPage           (faqs[])
//   - JSON-LD HowTo             (howTo.steps[])
//
// Guidelines we follow:
//   - Titles target "<verb> <format>" head terms users actually search for
//     ("Merge PDF", "Compress PDF", "JPG to PDF") + a trust modifier
//     ("Free", "Online", "No Sign-up") + brand.
//   - Descriptions: first sentence = what & who for, second = differentiator
//     (browser-based / no upload / privacy). Keep under 160 chars.
//   - FAQs answer real PAA ("People Also Ask") patterns: is it free? is it
//     safe? does it keep quality? file size limits? which browsers?
//   - HowTo steps match the exact UI flow so the rich result doesn't mislead.

import { TOOLS, getToolBySlug } from './registry';

const BRAND = 'TinyPDFs';

// --- Conversion helper -----------------------------------------------------
// Generates a solid SEO pack for any "<src> to <dst>" style tool. Tuned to
// read naturally rather than feeling templated.
function conversionSeo({ slug, name, srcLabel, dstLabel, srcExt, dstExt, verbLong = 'Convert' }) {
  const verb = verbLong;
  const url = `/tools/${slug}`;
  const plural = srcExt === 'PDF' ? 'documents' : 'files';
  return {
    title: `${srcLabel} to ${dstLabel} — Free Online ${verb}er | ${BRAND}`,
    description: `${verb} ${srcLabel} to ${dstLabel} online for free. Fast, browser-based, no sign-up, no watermarks. Your ${plural} stay private on your device.`,
    h1: `${srcLabel} to ${dstLabel} Converter`,
    keywords: [
      `${srcLabel.toLowerCase()} to ${dstLabel.toLowerCase()}`,
      `convert ${srcLabel.toLowerCase()} to ${dstLabel.toLowerCase()}`,
      `${srcExt.toLowerCase()} to ${dstExt.toLowerCase()}`,
      `${srcExt.toLowerCase()} to ${dstExt.toLowerCase()} converter`,
      `online ${srcLabel.toLowerCase()} to ${dstLabel.toLowerCase()}`,
      `free ${srcExt.toLowerCase()} to ${dstExt.toLowerCase()}`,
    ],
    intro: `${name} lets you ${verb.toLowerCase()} ${srcLabel} ${plural} into ${dstLabel} right in your browser. No installs, no uploads to a server, no watermarks — just open the file, convert, and download.`,
    faqs: [
      {
        q: `How do I convert ${srcLabel} to ${dstLabel} online?`,
        a: `Open the ${name} tool, drop your ${srcExt} file into the picker, and click Convert. Your ${dstExt} is ready to download in seconds — all processed in your browser.`,
      },
      {
        q: `Is the ${srcLabel}-to-${dstLabel} converter free?`,
        a: `Yes. ${BRAND} is 100% free with no sign-up, no watermark, and no daily limits for normal use.`,
      },
      {
        q: `Are my files safe?`,
        a: `Your files never leave your device. ${name} runs entirely in the browser, so nothing is uploaded to our servers.`,
      },
      {
        q: `Does it keep the original quality?`,
        a: `Yes — ${name} preserves layout and resolution. For images, original pixels are kept; for documents, fonts and spacing are retained wherever the source format allows.`,
      },
    ],
    howTo: {
      name: `How to convert ${srcLabel} to ${dstLabel}`,
      description: `Turn ${srcLabel} ${plural} into ${dstLabel} in four quick steps with ${BRAND}.`,
      totalTime: 'PT1M',
      steps: [
        { name: `Open ${name}`, text: `Go to ${BRAND} and open the ${name} tool at tinypdfs.com${url}.` },
        { name: 'Add your file', text: `Drag and drop your ${srcExt} file onto the picker, or click to browse.` },
        { name: 'Adjust options', text: `Pick page size, orientation, or image quality if prompted. Defaults work for most files.` },
        { name: 'Convert', text: `Click Convert. The ${dstExt} is generated entirely in your browser — no upload.` },
        { name: 'Download', text: `Save the ${dstExt} to your device. Repeat with another file or close the tab when done.` },
      ],
    },
  };
}

// --- Hand-written entries for the top tools --------------------------------
// Writing these ourselves (vs. templating) is what actually differentiates us
// on SERPs for the highest-volume queries.

const HAND_WRITTEN = {
  pdfmerger: {
    title: 'Merge PDF Files Online Free — No Sign-up | TinyPDFs',
    description: 'Combine multiple PDFs into one file in seconds. Free online PDF merger, no watermark, no uploads — everything runs in your browser.',
    h1: 'Merge PDF Files Online',
    keywords: [
      'merge pdf', 'combine pdf', 'join pdf', 'pdf merger', 'merge pdfs online',
      'combine pdf files', 'merge pdf free', 'pdf combiner',
    ],
    intro: 'PDF Merger joins multiple PDF documents into a single file. Reorder pages with drag-and-drop, add or remove files, and export a combined PDF — all in your browser with zero uploads.',
    faqs: [
      { q: 'How do I merge PDFs online for free?',
        a: 'Drop your PDFs into the merger, drag them into the order you want, and click Merge. The combined PDF downloads instantly — no sign-up, no watermark.' },
      { q: 'Is there a file-size or page limit?',
        a: 'No hard limit. Because merging happens in your browser, the practical ceiling is your device’s memory — typically several hundred MB of PDFs combined.' },
      { q: 'Will the merged PDF keep bookmarks and links?',
        a: 'Yes. Internal links, outlines and metadata from each source PDF are preserved in the output wherever the source file exposes them.' },
      { q: 'Is it safe to merge confidential PDFs here?',
        a: 'Yes — nothing leaves your device. TinyPDFs runs the merge locally, so sensitive documents never touch our servers.' },
    ],
    howTo: {
      name: 'How to merge PDF files',
      description: 'Combine two or more PDFs into a single document with TinyPDFs.',
      totalTime: 'PT1M',
      steps: [
        { name: 'Open PDF Merger', text: 'Visit tinypdfs.com/tools/pdfmerger.' },
        { name: 'Add PDFs', text: 'Drop your PDF files onto the picker, or click to select them from your device.' },
        { name: 'Reorder', text: 'Drag the file tiles to the order you want them to appear in the merged document.' },
        { name: 'Merge', text: 'Click Merge. Your browser assembles the combined PDF locally.' },
        { name: 'Download', text: 'Save the merged PDF to your device.' },
      ],
    },
  },

  pdfsplitter: {
    title: 'Split PDF Online Free — Extract Pages | TinyPDFs',
    description: 'Split a PDF into separate pages or extract a page range. Free online PDF splitter — no watermark, no upload, runs in your browser.',
    h1: 'Split PDF & Extract Pages',
    keywords: [
      'split pdf', 'pdf splitter', 'extract pages from pdf', 'separate pdf pages',
      'pdf page extractor', 'divide pdf', 'split pdf free online',
    ],
    intro: 'PDF Splitter breaks a PDF into single pages or extracts a specific page range. Pick the pages you need, and download the result instantly — no upload required.',
    faqs: [
      { q: 'How do I split a PDF into separate pages?',
        a: 'Open the PDF Splitter, load your file, choose "Every page" or enter a range like 2-5, then click Split. Your pages download as a ZIP.' },
      { q: 'Can I extract just one page from a PDF?',
        a: 'Yes. Enter a single page number in the range field (e.g. 7) and click Split — you get a single-page PDF back.' },
      { q: 'Do I need to upload my PDF?',
        a: 'No. TinyPDFs splits the PDF entirely in your browser, so your file never touches our servers.' },
      { q: 'Does splitting change the original PDF?',
        a: 'No — your source file is untouched. The tool produces new PDFs alongside the original.' },
    ],
    howTo: {
      name: 'How to split a PDF',
      description: 'Extract pages or break a PDF into single-page files with TinyPDFs.',
      totalTime: 'PT1M',
      steps: [
        { name: 'Open PDF Splitter', text: 'Visit tinypdfs.com/tools/pdfsplitter.' },
        { name: 'Load your PDF', text: 'Drop a PDF onto the picker or click to browse.' },
        { name: 'Choose the split', text: 'Pick "Every page" to separate all pages, or enter a range like 3-7 to extract a subset.' },
        { name: 'Split', text: 'Click Split. Pages are carved out locally in your browser.' },
        { name: 'Download', text: 'Save the resulting PDF(s) or ZIP to your device.' },
      ],
    },
  },

  pdfcompress: {
    title: 'Compress PDF Online Free — Reduce PDF Size | TinyPDFs',
    description: 'Compress PDFs locally with Basic or Smart mode. Compare file size and quality before downloading; Smart mode converts pages to images.',
    h1: 'Compress PDF & Reduce File Size',
    keywords: [
      'compress pdf', 'reduce pdf size', 'pdf compressor', 'shrink pdf',
      'make pdf smaller', 'compress pdf online', 'pdf size reducer',
    ],
    intro: 'Compress PDF offers two different trade-offs. Basic rewrites the PDF structure and removes metadata while preserving selectable text. Smart renders pages as JPEG images, which can reduce size but removes text selection and can affect links, forms, and accessibility. Keep the original and inspect the result before sharing. Processing takes place in your browser.',
    faqs: [
      { q: 'How much can TinyPDFs compress a PDF?',
        a: 'Savings depend on the original document and settings; there is no guaranteed percentage. Already optimized PDFs may become larger. Compare the original and output sizes shown after processing.' },
      { q: 'Will compression blur my images?',
        a: 'Smart mode rasterizes every page. Lower quality settings may blur images and small text, and text is no longer selectable. Basic mode preserves text selection but may achieve little reduction.' },
      { q: 'Is there a PDF size limit?',
        a: 'Available device memory limits processing. Large or image-heavy files can fail, especially on phones. Try a smaller document and keep the original if processing fails.' },
      { q: 'Is it free and safe?',
        a: 'Yes. It’s free with no sign-up, and the compression runs locally — your PDF never leaves your device.' },
    ],
    howTo: {
      name: 'How to compress a PDF',
      description: 'Choose the trade-off between file size, image quality, and selectable text.',
      totalTime: 'PT1M',
      steps: [
        { name: 'Open Compress PDF', text: 'Visit tinypdfs.com/tools/pdfcompress.' },
        { name: 'Add your PDF', text: 'Drop a PDF onto the picker or click to browse.' },
        { name: 'Pick a preset', text: 'Choose Basic to preserve selectable text, or Smart to convert pages to images. In Smart mode, select Low, Medium, or High quality.' },
        { name: 'Compress', text: 'Click Compress. Your browser rewrites the PDF locally.' },
        { name: 'Download', text: 'Compare output size, open the result, and check small text and important pages before using it. Keep your original file.' },
      ],
    },
  },

  pdfwatermark: {
    title: 'Add Watermark to PDF — Free Online Tool | TinyPDFs',
    description: 'Add text or image watermarks to any PDF. Free, browser-based watermarking with opacity, position, rotation — no upload, no sign-up.',
    h1: 'Add a Watermark to a PDF',
    keywords: [
      'pdf watermark', 'add watermark to pdf', 'watermark pdf online',
      'pdf watermark tool', 'stamp pdf', 'free pdf watermark',
    ],
    intro: 'PDF Watermark stamps a text or image overlay onto every page of a PDF. Control opacity, rotation, position and page range — handy for drafts, confidentiality notices and branding.',
    faqs: [
      { q: 'Can I add an image watermark (logo) to a PDF?',
        a: 'Yes. Switch to Image mode, upload a PNG or JPG, and it will be stamped on every page you select.' },
      { q: 'Can I watermark only some pages?',
        a: 'Yes — use the page range field (e.g. 1-3,7) to limit the stamp to specific pages.' },
      { q: 'Will the watermark be removable?',
        a: 'The watermark is drawn into the page content, so it behaves like part of the PDF. Viewers can’t toggle it off.' },
      { q: 'Does the tool upload my PDF?',
        a: 'No. Watermarking happens entirely in your browser.' },
    ],
    howTo: {
      name: 'How to watermark a PDF',
      description: 'Add a text or image watermark to any PDF with TinyPDFs.',
      totalTime: 'PT2M',
      steps: [
        { name: 'Open PDF Watermark', text: 'Visit tinypdfs.com/tools/pdfwatermark.' },
        { name: 'Load your PDF', text: 'Drop a PDF onto the picker.' },
        { name: 'Choose text or image', text: 'Type the watermark text, or upload a logo image.' },
        { name: 'Style it', text: 'Set position, opacity, rotation and page range.' },
        { name: 'Apply', text: 'Click Apply. Preview each page before downloading.' },
        { name: 'Download', text: 'Save the watermarked PDF.' },
      ],
    },
  },

  pdfrotator: {
    title: 'Rotate PDF Pages Online Free — PDF Rotator | TinyPDFs',
    description: 'Rotate individual PDF pages 90°, 180°, or 270° and save the corrected file. Free online rotator — no upload, no watermark.',
    h1: 'Rotate PDF Pages',
    keywords: [
      'rotate pdf', 'pdf rotator', 'rotate pdf page', 'rotate pdf online',
      'turn pdf page', 'fix pdf orientation',
    ],
    intro: 'PDF Rotator fixes sideways or upside-down pages. Rotate individual pages or the whole document, then save a corrected copy — all offline in your browser.',
    faqs: [
      { q: 'Can I rotate just one page of a PDF?',
        a: 'Yes. Click the rotation button on any page thumbnail to turn only that page.' },
      { q: 'Will the rotation stick after saving?',
        a: 'Yes — rotations are baked into the saved PDF, so every viewer (including browsers and email previews) shows the pages the right way up.' },
      { q: 'Is it free?',
        a: 'Yes. No sign-up, no watermark, no daily limit.' },
    ],
    howTo: {
      name: 'How to rotate a PDF page',
      description: 'Fix the orientation of PDF pages with TinyPDFs.',
      totalTime: 'PT1M',
      steps: [
        { name: 'Open PDF Rotator', text: 'Visit tinypdfs.com/tools/pdfrotator.' },
        { name: 'Load your PDF', text: 'Drop a PDF onto the picker.' },
        { name: 'Rotate pages', text: 'Click the rotate button under any page thumbnail to turn it 90° at a time.' },
        { name: 'Save', text: 'Click Save. The rotated PDF downloads to your device.' },
      ],
    },
  },

  pdftoword: {
    title: 'PDF to Word Converter Online Free — Convert PDF to DOCX | TinyPDFs',
    description: 'Convert PDF to Word online, free. Turn any PDF into an editable .docx document right in your browser — no sign-up, no upload, fully private.',
    h1: 'PDF to Word',
    keywords: [
      'pdf to word', 'pdf to docx', 'convert pdf to word',
      'pdf to word converter', 'pdf to word online free',
      'free pdf to word converter', 'pdf to word free',
    ],
    intro:
      'Convert PDF to Word right in your browser with three conversion modes. Editable text extracts clean paragraphs from any PDF (with automatic OCR for scans in 11 languages). Visual embeds each PDF page as a high-resolution image so the Word copy looks pixel-identical to your PDF — ideal for invoices and branded documents. Hybrid gives you both: the image for layout, followed by the editable text. Nothing is uploaded; conversion runs entirely on your device and the output opens in Word, Google Docs, Pages, or LibreOffice.',
    faqs: [
      { q: 'Is this PDF to Word converter really free?',
        a: 'Yes. No sign-up, no watermark, no daily limit. The whole conversion runs in your browser, so there is nothing for us to charge for.' },
      { q: 'Will my PDF be uploaded anywhere?',
        a: 'No. TinyPDFs processes the PDF entirely on your device using pdf.js, the docx library, and Tesseract.js for OCR. Your file never leaves the browser tab — which is also why it works offline after the page has loaded.' },
      { q: 'Can it convert scanned PDFs or images of pages?',
        a: 'Yes. Scanned PDFs go through built-in OCR powered by Tesseract.js in your browser. OCR is automatic by default, but you can force "Always OCR" for PDFs with a garbled text layer, or "Never" for maximum speed. 11 languages are supported, including English, Spanish, French, German, Russian, Chinese, Japanese, and Arabic.' },
      { q: 'Does the Word file keep fonts, images, columns, and tables?',
        a: 'That depends on the mode. "Editable text" mode produces clean, fully-editable paragraphs but does not preserve fonts, colors, images or tables — no browser-only tool reconstructs layouts reliably. "Visual" mode embeds each PDF page as a high-resolution image, so the Word file looks pixel-identical to your PDF (text inside the image is not directly editable). "Hybrid" mode gives you both: the image for layout fidelity, then the editable text on the next page.' },
      { q: 'Which conversion mode should I pick?',
        a: 'Pick "Editable text" when you mainly need to change the wording (reports, articles, plain documents). Pick "Visual" when the Word copy must look like the PDF — typical for invoices, certificates, branded letters, receipts. Pick "Hybrid" when you want the layout to match AND the ability to edit the text separately; it produces a longer document but covers both needs.' },
      { q: 'How accurate is the OCR?',
        a: 'Tesseract is the same open-source OCR engine used behind the scenes by many commercial tools. On clean 300-DPI scans of printed text it typically hits 95%+ accuracy; on handwriting or low-resolution faxes it struggles like any OCR. Higher-quality scans give better results.' },
      { q: 'What Word version does the output work with?',
        a: 'It produces a standard .docx that opens in Microsoft Word 2007 and newer, Word for Mac, Word Online, Google Docs, Apple Pages, and LibreOffice Writer.' },
    ],
    howTo: {
      name: 'How to convert PDF to Word',
      description: 'Turn a PDF into an editable Word document with TinyPDFs.',
      totalTime: 'PT1M',
      steps: [
        { name: 'Open PDF to Word',
          text: 'Go to tinypdfs.com/tools/pdftoword.' },
        { name: 'Drop your PDF',
          text: 'Click "Choose PDF" or drag a PDF file onto the upload area. Large files are fine — everything stays on your device.' },
        { name: 'Pick the pages to convert',
          text: 'Convert the whole document by default, or enter ranges like "1-3, 5, 8-10" to extract only what you need.' },
        { name: 'Choose options',
          text: 'Decide whether to insert a page break between PDF pages and set the output filename.' },
        { name: 'Click "Convert to Word"',
          text: 'TinyPDFs extracts the text layer, rebuilds each paragraph, and packages everything into a clean .docx.' },
        { name: 'Download and edit',
          text: 'Open the downloaded Word document in Microsoft Word, Google Docs, Pages, or LibreOffice and edit freely.' },
      ],
    },
  },
};

// --- Conversion overrides (names per tool) ---------------------------------
// For the 13 conversion tools, lean on the helper with human-friendly labels.

const CONVERSION_MAP = {
  imagetopdf: { srcLabel: 'Image',  dstLabel: 'PDF', srcExt: 'image', dstExt: 'PDF' },
  jpgtopdf:   { srcLabel: 'JPG',    dstLabel: 'PDF', srcExt: 'JPG',   dstExt: 'PDF' },
  pngtopdf:   { srcLabel: 'PNG',    dstLabel: 'PDF', srcExt: 'PNG',   dstExt: 'PDF' },
  bmptopdf:   { srcLabel: 'BMP',    dstLabel: 'PDF', srcExt: 'BMP',   dstExt: 'PDF' },
  webptopdf:  { srcLabel: 'WebP',   dstLabel: 'PDF', srcExt: 'WebP',  dstExt: 'PDF' },
  txttopdf:   { srcLabel: 'TXT',    dstLabel: 'PDF', srcExt: 'TXT',   dstExt: 'PDF' },
  htmltopdf:  { srcLabel: 'HTML',   dstLabel: 'PDF', srcExt: 'HTML',  dstExt: 'PDF' },
  wordtopdf:  { srcLabel: 'Word',   dstLabel: 'PDF', srcExt: 'DOCX',  dstExt: 'PDF' },
  exceltopdf: { srcLabel: 'Excel',  dstLabel: 'PDF', srcExt: 'XLSX',  dstExt: 'PDF' },
  pdftojpg:   { srcLabel: 'PDF',    dstLabel: 'JPG', srcExt: 'PDF',   dstExt: 'JPG',  verbLong: 'Convert' },
  pdftopng:   { srcLabel: 'PDF',    dstLabel: 'PNG', srcExt: 'PDF',   dstExt: 'PNG',  verbLong: 'Convert' },
  pdftowebp:  { srcLabel: 'PDF',    dstLabel: 'WebP', srcExt: 'PDF',  dstExt: 'WebP', verbLong: 'Convert' },
  pdftotxt:   { srcLabel: 'PDF',    dstLabel: 'TXT', srcExt: 'PDF',   dstExt: 'TXT',  verbLong: 'Extract' },
  pdftohtml:  { srcLabel: 'PDF',    dstLabel: 'HTML', srcExt: 'PDF',  dstExt: 'HTML', verbLong: 'Convert' },
};

// --- Public API ------------------------------------------------------------

export function getToolSeo(slug) {
  const tool = getToolBySlug(slug);
  if (!tool) return null;

  if (HAND_WRITTEN[slug]) {
    return { ...HAND_WRITTEN[slug], url: `/tools/${slug}`, name: tool.name, slug };
  }
  const conv = CONVERSION_MAP[slug];
  if (conv) {
    return {
      ...conversionSeo({ slug, name: tool.name, ...conv }),
      url: `/tools/${slug}`,
      name: tool.name,
      slug,
    };
  }
  // Graceful fallback: still meaningful, still keyword-bearing.
  return {
    title: `${tool.name} — Free Online Tool | ${BRAND}`,
    description: `${tool.short} Free, browser-based and private — no sign-up, no upload, no watermark.`,
    h1: tool.name,
    keywords: [tool.name.toLowerCase(), 'pdf', 'online', 'free'],
    intro: `${tool.short} Runs entirely in your browser, so your files stay on your device.`,
    faqs: [
      { q: `Is ${tool.name} free?`, a: `Yes. ${BRAND} is free with no sign-up and no watermark.` },
      { q: `Do I have to upload my file?`, a: `No. ${tool.name} processes your file locally in the browser.` },
    ],
    howTo: null,
    url: `/tools/${slug}`,
    name: tool.name,
    slug,
  };
}

export function getDashboardSeo() {
  return {
    title: `PDF Tools Online — Free, No Sign-up | ${BRAND}`,
    description: 'Every PDF tool you need in one place: merge, split, compress, rotate, watermark, and convert PDFs to and from JPG, PNG, Word, Excel, HTML and more.',
    h1: 'Free Online PDF Tools',
    keywords: [
      'pdf tools', 'free pdf tools', 'online pdf tools', 'pdf toolkit',
      'pdf editor online', 'pdf utilities',
    ],
    url: '/tools',
  };
}

export function getHomeSeo() {
  return {
    title: `${BRAND} — Free Online PDF Tools & Concise PDF Library`,
    description: 'Free, browser-based PDF tools (merge, split, compress, convert) plus a curated library of concise two-page PDFs for creators, founders and lifelong learners.',
    keywords: [
      'pdf tools', 'free pdf tools', 'online pdf editor', 'pdf library',
      'two-page pdf', 'tinypdfs',
    ],
    url: '/',
  };
}

// Pre-compute a slug list so consumers (sitemap proxy, tests) don't re-import
// the heavy registry.
export const ALL_TOOL_SLUGS = TOOLS.map((t) => t.slug);
